import { ftPaintedWallForCast } from '../../port/k2-geometry/painted_wall_cast.js';
import { ftPaintedWallGeometry, ftPaintedWallLength, ftPaintedWallIntersectsSphere } from '../../port/k2-geometry/painted_wall_geometry.js';
import { ftPaintedWallContactsCloud } from '../../port/k2-geometry/painted_wall_clouds.js';
import { ftShapeSegmentHit3d } from '../../port/k2-geometry/ft3d_shape_core.js';
import { array, native } from '../../port/k2-geometry/php-shapes.js';
import { geometryEnvironment } from '../../geometry/port-environment.js';
import { wallIntersectsCylinder, type PaintedWallGeometry } from '../../../shared/painted-wall-geometry.js';
import { definitionNotHeld, handBack, itemField, record, refuse, upper, type Row, type JanusCtx } from '../context.js';
import type { JanusInput } from '../janus.js';
import { statusRows, statusPaused } from '../../statuses/expiry.js';
import { zoneIsDepthBearing } from '../../zones/zone-membership.js';
import { liftWeight } from '../../inventory/wind-wall.js';
import { readInventoryTransform } from '../../compile/inventory-transform.js';
import { computedValue } from '../../../shared/array-types.js';
import type { PlacedAoePlacement, PlacedAoeReaders } from './expand-placed-aoe-targets.js';
import { ICE_WALL } from './painted-wall-ice-cast';
import type { IceWallDisplacementReaders } from './painted-wall-ice-displacement-state';

export const WIND_SENSITIVE_CLOUDS = new Set(['CED0CFCC-937C-DD0B-1ED2-8B2874AB1887', 'BF05202C-57C5-A0F2-6BF3-B2A1276D007D',
  '622781B5-6890-09CE-6E82-376BF0E23133']);

/** The existing cast port owns path/attachment/dimension/endpoint validation. The confirmed scene owns aim/range/sight. */
export function paintedWallForPlacement(read: Pick<PlacedAoeReaders, 'value'>, placed: PlacedAoePlacement, casterId: string,
  ability: Row): PaintedWallGeometry | null {
  if (placed.paintedWall == null) return null;
  const env = geometryEnvironment({
    item_field: (row: unknown, key: string) => itemField(record(native(row)), key),
    layer_value: (row: unknown, key: string) => read.value(record(native(row)), key),
    janus_validation_veto: refuse,
    ft_painted_wall_geometry: ftPaintedWallGeometry,
    ft_painted_wall_length: ftPaintedWallLength,
    is_target_encounter_format: (token: string) => /^[0-9A-F-]{36}\|[^|,]+$/i.test(token),
    parse_encounter_target_string: (token: string) => {
      const coords = token.split('|')[1]?.split(':');
      return array(Object.fromEntries(['origin', 'distance'].flatMap((end, i) => ['x', 'y', 'z']
        .map((axis, j) => ['status_' + end + '_point_' + axis, coords?.length === 6 ? coords[i * 3 + j] : null]))));
    },
  });
  env.globals.ft_painted_wall_wire = array(placed.paintedWall);
  const geometry = ftPaintedWallForCast(placed.targetId, casterId, array(ability), env);
  if (!geometry) handBack('ft_painted_wall_for_cast:placement-mismatch');
  return native(geometry) as PaintedWallGeometry;
}

/** ft_painted_wall_target_intersection: the same physical volume used by painted walk/entry gates, over the full ribbon. */
export function paintedWallIntersectsTarget(read: PlacedAoeReaders, wall: PaintedWallGeometry, row: Row): boolean {
  const volume = paintedWallTargetVolume(read, row);
  if (volume === null) return false;
  const radius = volume.r ?? Math.max(volume.halfW ?? 0, volume.halfD ?? 0);
  return wallIntersectsCylinder(wall, { x: volume.x, y: volume.y, z: volume.zMin }, radius, volume.zMax - volume.zMin);
}

export function paintedWallTargetVolume(read: PlacedAoeReaders, row: Row) {
  // On unauthored scenery there is no structural volume. The page's occupancy reader still supplies the same physical body.
  const spatial = read.spatialVolume(row), volume = spatial === undefined ? read.occupancy(row) : spatial;
  if (volume === undefined) handBack('ft_painted_wall_target_intersection:scene-volume-missing');
  return volume!;
}

/** Exact cloud contact, including authored room sight, from the existing geometry port. */
export function paintedWallContactsCloud(wall: PaintedWallGeometry, status: Row, shape: Row | null): boolean {
  const env = geometryEnvironment({});
  return ftPaintedWallContactsCloud(array(wall), array(status), shape ? array(shape) : null, { deps: {
    ft_status_expiry_row_paused: row => statusPaused(record(native(row))!),
    ft_painted_wall_wind_sensitive_cloud: id => WIND_SENSITIVE_CLOUDS.has(upper(id)),
    persistent_aoe_status_is_depth_bearing: row => zoneIsDepthBearing({ status: record(native(row))!, key: '', holderId: '' }),
    ft_painted_wall_intersects_sphere: ftPaintedWallIntersectsSphere,
    ft_shape_sight_segment_hit_3d: (shape, source, target, contain) => ftShapeSegmentHit3d(shape, source, target, 'sight', contain, env),
  } });
}

/** Cast-only duties which are separate from ordinary damage/status dispatch. Refuse an unsupported duty only when it is due. */
export function paintedWallCastPreDraw(ctx: JanusCtx, input: JanusInput): void {
  if (ctx.trigger || input.paintedWall?.abilityId !== upper(input.abilityId)) return;
  const { geometry } = input.paintedWall;
  const ice = input.abilityId === ICE_WALL;
  const wind = input.abilityId === 'B9032A2F-B68E-0D7E-A234-90CAD8959892';
  const prismatic = input.abilityId === 'FC95B5FB-B47D-7848-F956-06663487666C';
  if (!wind && !prismatic && !ice) return;
  const read = (ctx.read as typeof ctx.read & { placedAoe?: PlacedAoeReaders }).placedAoe;
  if (!read) handBack('ft_painted_wall_for_cast:scene-readers-missing');
  if (ice) {
    if (!ctx.read.iceWallSections) handBack('ft_painted_wall_ice_cast_sections:source-package-not-prepared');
    const prepare = (ctx.read as typeof ctx.read & IceWallDisplacementReaders).prepareIceWallDisplacements;
    if (prepare) prepare(ctx, geometry, read!);
    else if ([...ctx.members.values()].some(row => paintedWallIntersectsTarget(read!, geometry, row)))
      handBack('ft_painted_wall_ice_cast_sections:displacement-readers-missing');
    return;
  }
  // Authored Prismatic Wall: "the spell fails if the wall would pass through a creature's space when created".
  // This judges every body, including allies and the caster, before the spell's opposing-team effect filter.
  if (prismatic) {
    if ([...ctx.members.values()].some(row => paintedWallIntersectsTarget(read!, geometry, row)))
      refuse("Prismatic Wall cannot be created through a creature's space.");
    return;
  }
  for (const status of statusRows(ctx.encounter, 'target')) {
    if (!WIND_SENSITIVE_CLOUDS.has(upper(status.status_origin_id ?? status.status_item_id))
        || Number(status.status_aoe_source ?? 0) !== 1 || statusPaused(status)) continue;
    if (read!.sceneShape === undefined) handBack('ft_painted_wall_disperse_existing_clouds:scene-shape-missing');
    if (paintedWallContactsCloud(geometry, status, read!.sceneShape ?? null))
      handBack('ft_painted_wall_disperse_existing_clouds:connection-cleanup-unported');
  }
  const encounterId = upper(record(ctx.encounter.encounters)?.encounter_id);
  for (const row of Array.isArray(ctx.encounter.encounter_inventories_arr) ? ctx.encounter.encounter_inventories_arr as Row[] : []) {
    const field = (key: string) => read!.inventoryField(row, key);
    if (!paintedWallIntersectsTarget(read!, geometry, row)) continue;
    const itemId = upper(field('inventory_item_id')), source = ctx.read.definition(itemId) ?? (itemField(row, 'item_type') ? row : null);
    if (!source) definitionNotHeld('ft_painted_wall_wind_lift_cast:definition-not-held', itemId);
    const weight = liftWeight({ possessId: upper(field('possess_id')), encounterId, deleted: Number(field('inventory_deleted') ?? 0) !== 0,
      private: Number(field('inventory_private') ?? 0) !== 0, qty: Number(field('item_qty') ?? 1), equipped: !!Number(field('equipped') ?? 0),
      immovable: !!ctx.read.value(source, 'immovable'), mounted: readInventoryTransform(row)?.mounted === true,
      itemType: String(itemField(source, 'item_type') ?? ''), sizeId: upper(field('inventory_size') ?? ctx.read.value(source, 'size')),
      grossLb: computedValue(row, 'weight', 'gross') ?? computedValue(row, 'weight', 'total') ?? computedValue(source, 'weight', 'total') ?? null,
      unitWeightLb: ctx.read.value(source, 'weight') == null ? null : Number(ctx.read.value(source, 'weight')) });
    if (weight !== null)
      handBack('ft_painted_wall_wind_lift_cast:loose-inventory-settlement-unported');
  }
}
